import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import ModaleImpianto from '../components/ModaleImpianto';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getApiBase } from '../services/api';
import { salvaBozza, leggiBozza, rimuoviBozza, postSicuro, putSicuro,
         avviaSincronizzazione, operazioniInAttesa } from '../services/offline';

const TIPO_LABEL = {
  estintori:'🧯 Estintori', idranti:'🚒 Idranti/Naspi', porte:'🚪 Porte REI/U.S./Portoni scorrevoli',
  rivelazione_fumi:'💨 Rilevazione fumi', sprinkler:'💧 Sprinkler',
  illuminazione_emergenza:'💡 Luci di emergenza',
  gruppi_pompa:'⚙️ Gruppi pompa', cassette_mediche:'🩺 Cassette mediche', dpi:'🦺 DPI',
};

export default function InterventoDettaglio() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [fase, setFase]     = useState('checklist'); // checklist | firma | completato
  const [items, setItems]   = useState([]);
  const [saving, setSaving] = useState(false);
  const [nomeFirma, setNome]= useState('');
  const canvasRef        = useRef(null);
  const canvasTecnicoRef = useRef(null);
  const [disegno, setDisegno]     = useState(false);
  const [nomeTecnico, setNomeTecnico] = useState('');
  const [materiali, setMateriali] = useState([]);
  const [prodottiFic, setProdFic] = useState([]);
  const [cercaProd, setCercaProd] = useState('');
  const [dttLoading, setDttLoad] = useState(false);
  const [firmaDdtOpen, setFirmaDdtOpen] = useState(false);
  const [impiantiSpot, setImpiantiSpot] = useState([]);
  const { user } = useAuth();
  const isAdmin = (user?.ruolo || '').toLowerCase() === 'admin';
  const [modImpianto, setModImpianto] = useState(null);
  const [tipiEsclusi, setTipiEsclusi]   = useState([]);
  const [modaleSpot, setModaleSpot]     = useState(false);
  const [letteraLoading, setLetteraLoad] = useState(false);
  const [bozzaLoading, setBozzaLoad] = useState(false);
  const [rete, setRete] = useState({ online: navigator.onLine, inAttesa: operazioniInAttesa() });
  const caricamentoFatto = useRef(false);

  useEffect(() => avviaSincronizzazione(setRete), []);

  const carica = useCallback(async () => {
    try {
      const res = await api.get(`/intervento/${id}/impianti`);
      setData(res.data);
      // Appiattisci tutti gli impianti in lista unica con stato checklist
      const lista = [];
      const imp = res.data.impianti || {};
      Object.entries(imp).forEach(([tipo, arr]) => {
        (arr || []).forEach(item => lista.push({ ...item, tipo_impianto: tipo }));
      });
      setItems(lista);
      if (res.data.intervento?.stato === 'completato') setFase('completato');
      setOraInizio(res.data.intervento?.ora_inizio?.substring(0,5) || '');
      setOraFine(res.data.intervento?.ora_fine?.substring(0,5)     || '');
      setNoteTecnico(res.data.intervento?.note_tecnico || '');
      // Carica materiali salvati
      const spotRes = await api.get(`/intervento/${id}/impianti-spot`).catch(()=>({data:{data:[]}}));
      setImpiantiSpot(spotRes.data?.data || []);
      const esc = res.data.intervento?.tipi_esclusi;
      setTipiEsclusi(esc ? esc.split(',').map(s=>s.trim()).filter(Boolean) : []);

      const matRes = await api.get(`/intervento/${id}/materiali`).catch(()=>({data:{data:[]}}));
      setMateriali(matRes.data?.data || []);

      // Nome del tecnico e del firmatario già registrati
      const fRes = await api.get(`/intervento/${id}/firma`).catch(()=>({data:{}}));
      const fSalv = fRes.data?.firma;
      if (fSalv?.nome_tecnico)    setNomeTecnico(fSalv.nome_tecnico);
      if (fSalv?.nome_firmatario) setNome(fSalv.nome_firmatario);

      // Modifiche rimaste sul dispositivo e non ancora inviate
      const bozza = leggiBozza(id);
      if (bozza?.dati && res.data.intervento?.stato !== 'completato') {
        const quando = new Date(bozza.aggiornata).toLocaleString('it-IT');
        if (window.confirm(`Su questo dispositivo ci sono modifiche non inviate del ${quando}.\n\nVuoi riprenderle?`)) {
          if (bozza.dati.items?.length)  setItems(bozza.dati.items);
          if (bozza.dati.materiali)      setMateriali(bozza.dati.materiali);
          if (bozza.dati.noteTecnico != null) setNoteTecnico(bozza.dati.noteTecnico);
          if (bozza.dati.oraInizio)      setOraInizio(bozza.dati.oraInizio);
          if (bozza.dati.oraFine)        setOraFine(bozza.dati.oraFine);
          if (bozza.dati.nomeFirma)      setNome(bozza.dati.nomeFirma);
          if (bozza.dati.nomeTecnico)    setNomeTecnico(bozza.dati.nomeTecnico);
          // Ridisegna le firme conservate sul dispositivo
          setTimeout(() => {
            const disegna = (tela, dato) => {
              if (!tela || !dato) return;
              const img = new Image();
              img.onload = () => tela.getContext('2d').drawImage(img, 0, 0);
              img.src = dato;
            };
            disegna(canvasRef.current, bozza.dati.firmaCliente);
            disegna(canvasTecnicoRef.current, bozza.dati.firmaTecnico);
          }, 400);
        } else {
          rimuoviBozza(id);
        }
      }
      caricamentoFatto.current = true;
    } catch(e) { console.error(e); }
    finally { setLoad(false); }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  useEffect(() => {
    api.get('/preventivi/prodotti').then(r => setProdFic(r.data?.data || [])).catch(()=>{});
  }, []);

  const toggleItem = (idx) => {
    setItems(its => its.map((it, i) => i===idx ? {...it, controllato: !it.controllato} : it));
  };
  const setNote = (idx, val) => {
    setItems(its => its.map((it, i) => i===idx ? {...it, note: val} : it));
  };

  const [noteTecnico, setNoteTecnico] = useState('');
  const [oraInizio, setOraInizio]     = useState('');
  const [oraFine,   setOraFine]       = useState('');

  const spostaItem = async (idx, dir) => {
    setItems(its => {
      const arr   = [...its];
      const tipo  = arr[idx].tipo_impianto;
      // Trova gli indici degli item dello stesso tipo
      const tipoIdx = arr.map((it,i)=>it.tipo_impianto===tipo?i:-1).filter(i=>i>=0);
      const pos     = tipoIdx.indexOf(idx);
      const newPos  = pos + dir;
      if (newPos < 0 || newPos >= tipoIdx.length) return arr;
      // Scambia
      const tmp = arr[tipoIdx[pos]];
      arr[tipoIdx[pos]]    = arr[tipoIdx[newPos]];
      arr[tipoIdx[newPos]] = tmp;
      // Salva ordine sul server
      const cid = data?.intervento?.cliente_id;
      if (cid) {
        const ids = tipoIdx.map(i => arr[i].id);
        api.post(`/cliente/${cid}/ordine-impianti`, { tipo, ids }).catch(()=>{});
      }
      return arr;
    });
  };

  const aggMateriale = () => setMateriali(m => [...m, {descrizione:'',quantita:1,prezzo_unitario:0,prodotto_id:null}]);
  const rimuMateriale = i => setMateriali(m => m.filter((_,j)=>j!==i));
  const setMat = (i,k,v) => setMateriali(m => m.map((x,j)=>j===i?{...x,[k]:v}:x));

  const applicaProdFic = (i, prod) => {
    setMat(i, 'descrizione', prod.name);
    setMat(i, 'prodotto_id', prod.id);
    setMat(i, 'prezzo_unitario', parseFloat(prod.net_price||0));
    setCercaProd('');
  };

  const scaricaLettera = async () => {
    setLetteraLoad(true);
    try {
      const token = localStorage.getItem('quindi_token');
      const res = await fetch(`${getApiBase()}/api/intervento/${id}/lettera`, {
        headers: { 'Authorization': 'Bearer ' + token },
      });
      if (!res.ok) {
        const j = await res.json().catch(()=>({}));
        throw new Error(j.error || 'Errore generazione lettera');
      }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Lettera_${inv.sitoNome||inv.sito_nome||'cliente'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch(e) { alert('Errore: ' + e.message); }
    finally { setLetteraLoad(false); }
  };

  const preparaBozza = async () => {
    setBozzaLoad(true);
    try {
      const token = localStorage.getItem('quindi_token');
      const res = await fetch(`${getApiBase()}/api/intervento/${id}/bozza-email`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const j = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(j.error || 'Errore creazione bozza');
      alert(`✅ Bozza creata nella cartella Bozze della tua email.\nDestinatario: ${j.destinatario}\n\nApri il client email per rivederla e inviarla.`);
    } catch(e) { alert('Errore: ' + e.message); }
    finally { setBozzaLoad(false); }
  };

  const generaDdt = async () => {
    // Prima salva i materiali, poi apri il modale firma
    if (materiali.filter(m=>m.descrizione.trim()).length === 0) {
      alert('Nessun materiale da includere nel DDT.');
      return;
    }
    setFirmaDdtOpen(true);
  };

  const confermaDdtConFirma = async (firmaDataUrl) => {
    setDttLoad(true);
    setFirmaDdtOpen(false);
    try {
      await api.post(`/intervento/${id}/materiali`, materiali.filter(m=>m.descrizione.trim()));
      const res = await api.post(`/intervento/${id}/genera-ddt`, { firma: firmaDataUrl || null });
      // Scarica il PDF firmato se presente
      const pdfB64 = res.data?.pdfBase64;
      if (pdfB64) {
        const bytes = Uint8Array.from(atob(pdfB64), c=>c.charCodeAt(0));
        const blob = new Blob([bytes], { type:'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DDT_${res.data?.numero||''}_${inv.sitoNome||inv.sito_nome||'cliente'}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      }
      const ddt = res.data?.data?.data;
      if (ddt?.attachment_url) window.open(ddt.attachment_url, '_blank');
      alert('✅ DDT generato' + (pdfB64 ? ' e PDF firmato scaricato!' : ' su FIC!'));
    } catch(e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setDttLoad(false); }
  };

  // Conserva sul dispositivo ogni modifica, così nulla va perso senza rete
  useEffect(() => {
    if (!caricamentoFatto.current || fase === 'completato') return;
    const t = setTimeout(() => {
      salvaBozza(id, { items, materiali, noteTecnico, oraInizio, oraFine });
    }, 800);
    return () => clearTimeout(t);
  }, [id, items, materiali, noteTecnico, oraInizio, oraFine, fase]);

  // ── Impianti occasionali e gruppi esclusi ──
  const spuntaSpot = async (spotId, valore) => {
    setImpiantiSpot(l => l.map(s => s.id === spotId ? { ...s, controllato: valore } : s));
    try { await api.put(`/intervento-spot/${spotId}`, { controllato: valore }); }
    catch (e) { console.warn('spunta non salvata', e); }
  };

  const eliminaSpot = async (spotId) => {
    if (!window.confirm('Togliere questo impianto dall\'intervento?')) return;
    try {
      await api.delete(`/intervento-spot/${spotId}`);
      setImpiantiSpot(l => l.filter(s => s.id !== spotId));
    } catch (e) { alert('Errore: ' + e.message); }
  };

  const aggiungiSpot = async (dati) => {
    try {
      const r = await api.post(`/intervento/${id}/impianti-spot`, dati);
      setImpiantiSpot(l => [...l, { id: r.data?.id, controllato: 0, ...dati }]);
      setModaleSpot(false);
    } catch (e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const alternaEscluso = async (tipo) => {
    const nuovi = tipiEsclusi.includes(tipo)
      ? tipiEsclusi.filter(t => t !== tipo)
      : [...tipiEsclusi, tipo];
    setTipiEsclusi(nuovi);
    try { await api.post(`/intervento/${id}/tipi-esclusi`, { tipi: nuovi }); }
    catch (e) { console.warn('esclusione non salvata', e); }
  };

  const salvaChecklist = async () => {
    setSaving(true);
    try {
      const r1 = await postSicuro(`/intervento/${id}/checklist`, items);
      const r2 = await putSicuro(`/interventi/${id}`, {
        note_tecnico: noteTecnico || null,
        ora_inizio:   oraInizio   || null,
        ora_fine:     oraFine     || null,
      });
      if (r1.accodato || r2.accodato) {
        setRete({ online: navigator.onLine, inAttesa: operazioniInAttesa() });
        alert('Nessuna connessione: i dati sono salvati sul dispositivo e verranno inviati appena torna la rete.');
      }
      setFase('firma');
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  // Canvas firma
  useEffect(() => {
    if (fase !== 'firma' || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width  = canvas.offsetWidth;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth   = 2;
    ctx.lineCap     = 'round';

    let drawing = false, lastX = 0, lastY = 0;

    const getPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const src = e.touches ? e.touches[0] : e;
      return [src.clientX - r.left, src.clientY - r.top];
    };

    const start = (e) => { e.preventDefault(); drawing = true; [lastX, lastY] = getPos(e); };
    const draw  = (e) => {
      e.preventDefault();
      if (!drawing) return;
      const [x, y] = getPos(e);
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(x, y);
      ctx.stroke();
      [lastX, lastY] = [x, y];
    };
    const stop  = () => {
      drawing = false;
      // Conserva la firma sul dispositivo: se manca la rete o si spegne il tablet non va persa
      try {
        const c = canvasRef.current, t = canvasTecnicoRef.current;
        salvaBozza(id, { items, materiali, noteTecnico, oraInizio, oraFine,
          firmaCliente: c ? c.toDataURL('image/png') : null,
          firmaTecnico: t ? t.toDataURL('image/png') : null,
          nomeFirma, nomeTecnico });
      } catch (e) { console.warn('firma non conservata', e); }
    };

    canvas.addEventListener('mousedown',  start);
    canvas.addEventListener('mousemove',  draw);
    canvas.addEventListener('mouseup',    stop);
    canvas.addEventListener('touchstart', start, {passive:false});
    canvas.addEventListener('touchmove',  draw,  {passive:false});
    canvas.addEventListener('touchend',   stop);

    return () => {
      canvas.removeEventListener('mousedown',  start);
      canvas.removeEventListener('mousemove',  draw);
      canvas.removeEventListener('mouseup',    stop);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove',  draw);
      canvas.removeEventListener('touchend',   stop);
    };
  }, [fase]);

  // Setup canvas tecnico
  useEffect(() => {
    if (fase !== 'firma' || !canvasTecnicoRef.current) return;
    const canvas = canvasTecnicoRef.current;
    canvas.width  = canvas.offsetWidth;
    canvas.height = 120;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1f2937'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    let drawing = false, lastX = 0, lastY = 0;
    const getPos = (e) => { const r = canvas.getBoundingClientRect(); const src = e.touches ? e.touches[0] : e; return [src.clientX-r.left, src.clientY-r.top]; };
    const start = (e) => { e.preventDefault(); drawing=true; [lastX,lastY]=getPos(e); };
    const draw  = (e) => { e.preventDefault(); if(!drawing) return; const [x,y]=getPos(e); ctx.beginPath(); ctx.moveTo(lastX,lastY); ctx.lineTo(x,y); ctx.stroke(); [lastX,lastY]=[x,y]; };
    const stop  = () => { drawing=false; };
    canvas.addEventListener('mousedown',start); canvas.addEventListener('mousemove',draw); canvas.addEventListener('mouseup',stop);
    canvas.addEventListener('touchstart',start,{passive:false}); canvas.addEventListener('touchmove',draw,{passive:false}); canvas.addEventListener('touchend',stop);
    return () => { canvas.removeEventListener('mousedown',start); canvas.removeEventListener('mousemove',draw); canvas.removeEventListener('mouseup',stop); canvas.removeEventListener('touchstart',start); canvas.removeEventListener('touchmove',draw); canvas.removeEventListener('touchend',stop); };
  }, [fase]);

  const pulisciFirma = (tipo='cliente') => {
    const canvas = tipo==='tecnico' ? canvasTecnicoRef.current : canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  };

  const salvaFirma = async () => {
    const canvas        = canvasRef.current;
    const canvasTecnico = canvasTecnicoRef.current;
    if (!canvas) return;
    setSaving(true);
    try {
      const firmaBase64       = canvas.toDataURL('image/png');
      const firmaTecnicoBase64= canvasTecnico ? canvasTecnico.toDataURL('image/png') : null;

      // Conserva subito tutto sul dispositivo, prima ancora di provare l'invio
      salvaBozza(id, { items, materiali, noteTecnico, oraInizio, oraFine,
        firmaCliente: firmaBase64, firmaTecnico: firmaTecnicoBase64,
        nomeFirma, nomeTecnico, firmato: true });

      const rM = await postSicuro(`/intervento/${id}/materiali`, materiali.filter(m=>m.descrizione?.trim()));
      const rF = await postSicuro(`/intervento/${id}/firma`, {
        firma_base64:         firmaBase64,
        firma_tecnico_base64: firmaTecnicoBase64,
        nome_firmatario:      nomeFirma,
        nome_tecnico:         nomeTecnico,
        firmata_il:           new Date().toISOString(),
        firmata_offline:      !navigator.onLine,
      });

      if (rM.accodato || rF.accodato) {
        setRete({ online: navigator.onLine, inAttesa: operazioniInAttesa() });
        alert('Firma acquisita e salvata sul dispositivo.\n\n' +
              'Non c\'è connessione: verrà inviata automaticamente appena torna la rete. ' +
              'Non chiudere l\'applicazione finché l\'avviso non scompare.');
      }
      setFase('completato');
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const stampaPDF = () => { window.print(); };

  const inv = data?.intervento;
  const fmtData = d => d ? d.substring(0,10).split('-').reverse().join('/') : '—';
  const nTot  = items.length;
  const nOk   = items.filter(i => i.controllato).length;
  const perc  = nTot > 0 ? Math.round(nOk/nTot*100) : 0;

  // Raggruppa per tipo
  const itemsTutti = [...items, ...impiantiSpot.map(s => ({
    id: 'spot-' + s.id, spotId: s.id, spot: true,
    tipo_impianto: s.tipo_impianto,
    label: [s.descrizione, s.matricola, s.luogo].filter(Boolean).join(' | ') || 'Impianto',
    matricola: s.matricola, luogo: s.luogo, numero: s.numero,
    anno_produzione: s.anno, note: s.note,
    controllato: !!s.controllato,
  }))];

  const perTipo = itemsTutti.reduce((acc, it, idx) => {
    if (!acc[it.tipo_impianto]) acc[it.tipo_impianto] = [];
    acc[it.tipo_impianto].push({ ...it, _idx: idx });
    return acc;
  }, {});

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!inv)    return <div className="page-content"><p>Intervento non trovato.</p></div>;

  return (
    <div className="page-content" style={{ maxWidth: 700, margin: '0 auto', padding: '12px' }}>

      {/* HEADER */}
      <div className="no-print" style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={()=>navigate('/interventi')}>⬅</button>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:16 }}>{inv.sito_nome}</div>
          <div style={{ fontSize:12, color:'#6b7280' }}>{fmtData(inv.data_intervento)} · {inv.zona||'—'}</div>
        </div>
        <span className={`badge ${inv.stato==='completato'?'badge-green':inv.stato==='in_corso'?'badge-orange':'badge-blue'}`}>
          {inv.stato}
        </span>
      </div>

      {/* PROGRESS */}
      {(!rete.online || rete.inAttesa > 0) && (
        <div style={{
          padding:'8px 14px', borderRadius:8, marginBottom:12, fontSize:13, fontWeight:600,
          background: rete.online ? '#fef9c3' : '#fee2e2',
          color:      rete.online ? '#854d0e' : '#b91c1c',
          display:'flex', alignItems:'center', gap:8,
        }}>
          <span>{rete.online ? '⏳' : '📴'}</span>
          <span>
            {rete.online
              ? `Invio in corso: ${rete.inAttesa} modifiche in attesa`
              : `Offline — puoi continuare a lavorare${rete.inAttesa ? `, ${rete.inAttesa} modifiche in attesa` : ''}`}
          </span>
        </div>
      )}

      {fase === 'checklist' && nTot > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#6b7280', marginBottom:4 }}>
            <span>Progresso: {nOk}/{nTot}</span>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              {items.filter(i=>i.alert&&i.alert!=='OK'&&!i.controllato).length>0 && (
                <span style={{background:'#fee2e2',color:'#991b1b',padding:'1px 6px',borderRadius:4,fontSize:11,fontWeight:600}}>
                  ⚠️ {items.filter(i=>i.alert&&i.alert!=='OK'&&!i.controllato).length} anomalie
                </span>
              )}
              <span>{perc}%</span>
            </div>
          </div>
          <div style={{ background:'#e5e7eb', borderRadius:4, height:8, overflow:'hidden' }}>
            <div style={{ background:'#b30000', height:'100%', width:`${perc}%`, transition:'width .3s', borderRadius:4 }} />
          </div>
        </div>
      )}

      {/* CHECKLIST */}
      {fase === 'checklist' && (
        <div>
          {/* ORARI */}
          <div className="card" style={{ marginBottom:12, padding:'12px 16px' }}>
            <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
              <span style={{ fontWeight:600, fontSize:13 }}>⏱️ Orari</span>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <label style={{ fontSize:12, color:'#6b7280' }}>Inizio:</label>
                <input type="time" value={oraInizio} onChange={e=>setOraInizio(e.target.value)}
                  style={{ padding:'4px 8px', border:'1px solid #e5e7eb', borderRadius:4, fontSize:13 }} />
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <label style={{ fontSize:12, color:'#6b7280' }}>Fine:</label>
                <input type="time" value={oraFine} onChange={e=>setOraFine(e.target.value)}
                  style={{ padding:'4px 8px', border:'1px solid #e5e7eb', borderRadius:4, fontSize:13 }} />
              </div>
            </div>
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:10 }}>
            <button className="btn btn-secondary btn-sm" onClick={()=>setModaleSpot(true)}>
              ➕ Aggiungi impianto a questo intervento
            </button>
          </div>

          {Object.entries(perTipo).map(([tipo, lista]) => (
            <div key={tipo} style={{ marginBottom:16 }}>
              <div style={{ background:'#b30000', color:'#fff', padding:'8px 12px', borderRadius:'8px 8px 0 0', fontWeight:600, fontSize:13, display:'flex', justifyContent:'space-between' }}>
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}
                  title="Escludi questo gruppo dal verbale">
                  <input type="checkbox" checked={tipiEsclusi.includes(tipo)}
                    onChange={()=>alternaEscluso(tipo)}
                    onClick={e=>e.stopPropagation()} />
                  <span style={{ textDecoration: tipiEsclusi.includes(tipo) ? 'line-through' : 'none',
                                 opacity: tipiEsclusi.includes(tipo) ? .6 : 1 }}>
                    {TIPO_LABEL[tipo] || tipo}
                  </span>
                </label>
                <span style={{ opacity:.8, fontWeight:400, fontSize:12 }}>
                  {tipiEsclusi.includes(tipo)
                    ? 'escluso dal verbale'
                    : `${lista.filter(i=>i.controllato).length}/${lista.length}`}
                </span>
              </div>
              <div style={{ border:'1px solid #e5e7eb', borderTop:'none', borderRadius:'0 0 8px 8px', overflow:'hidden' }}>
                {lista.map((item) => (
                  <div key={item.id} style={{
                    borderBottom:'1px solid #f3f4f6',
                    background: item.controllato ? '#f0fdf4' : '#fff',
                    transition:'background .2s'
                  }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px' }}>
                      <div style={{ display:'flex', flexDirection:'column', gap:1, flexShrink:0 }}>
                        <button onClick={()=>spostaItem(item._idx,-1)}
                          disabled={lista.findIndex(x=>x._idx===item._idx)===0}
                          style={{ fontSize:10, padding:'0 4px', lineHeight:1.4, border:'1px solid #ddd', background:'#f8f8f8', cursor:'pointer', borderRadius:'3px 3px 0 0', opacity: lista.findIndex(x=>x._idx===item._idx)===0?0.3:1 }}>▲</button>
                        <button onClick={()=>spostaItem(item._idx,1)}
                          disabled={lista.findIndex(x=>x._idx===item._idx)===lista.length-1}
                          style={{ fontSize:10, padding:'0 4px', lineHeight:1.4, border:'1px solid #ddd', background:'#f8f8f8', cursor:'pointer', borderRadius:'0 0 3px 3px', opacity: lista.findIndex(x=>x._idx===item._idx)===lista.length-1?0.3:1 }}>▼</button>
                      </div>
                      <input type="checkbox" checked={item.controllato}
                        onChange={()=> item.spot ? spuntaSpot(item.spotId, !item.controllato) : toggleItem(item._idx)}
                        style={{ width:20, height:20, accentColor:'#b30000', cursor:'pointer', flexShrink:0 }} />
                      <div style={{ flex:1 }}>
                        {item.spot && (
                          <button onClick={e=>{e.stopPropagation(); eliminaSpot(item.spotId);}}
                            title="Togli da questo intervento"
                            style={{border:'none',background:'#fee2e2',color:'#dc2626',borderRadius:5,
                                    padding:'2px 6px',cursor:'pointer',fontSize:11,marginRight:6}}>🗑</button>
                        )}
                        {isAdmin && !item.spot && (
                          <button onClick={e=>{e.stopPropagation();
                            setModImpianto({ tipo: item.tipo_impianto, id: item.id });}}
                            title="Modifica impianto"
                            style={{border:'none',background:'#f1f5f9',borderRadius:5,
                                    padding:'2px 6px',cursor:'pointer',fontSize:11,marginRight:6}}>✏️</button>
                        )}
                        <span style={{ fontSize:13, color: item.controllato?'#374151':'#1f2937', textDecoration: item.controllato?'line-through':'none' }}>
                          {item.label}
                        </span>
                        {(item.anno_produzione || item.anno_revisione || item.anno_collaudo) && (
                          <div style={{ marginTop:2, fontSize:11, color:'#6b7280', display:'flex', gap:10, flexWrap:'wrap' }}>
                            {item.anno_produzione ? <span>📅 Fabbr. <strong>{item.anno_produzione}</strong></span> : null}
                            {item.anno_revisione  ? <span>🔧 Rev. <strong>{item.anno_revisione}</strong></span> : null}
                            {item.anno_collaudo   ? <span>🏷️ Coll. <strong>{item.anno_collaudo}</strong></span> : null}
                            {item.piano ? <span>🏢 Piano <strong>{item.piano}</strong></span> : null}
                            {item.anno_costruzione ? <span>📅 Anno <strong>{item.anno_costruzione}</strong></span> : null}
                          </div>
                        )}
                        {item.alert && item.alert !== 'OK' && !item.controllato && (
                          <div style={{ marginTop:2 }}>
                            {item.alert === 'SCADUTO' && <span style={{background:'#fee2e2',color:'#991b1b',fontSize:10,padding:'1px 6px',borderRadius:4,fontWeight:600}}>🔴 SCADUTO</span>}
                            {item.alert === 'IN_SCADENZA' && <span style={{background:'#fff7ed',color:'#92400e',fontSize:10,padding:'1px 6px',borderRadius:4,fontWeight:600}}>🟠 IN SCADENZA</span>}
                            {item.alert === 'REVISIONE_SCADUTA' && <span style={{background:'#fef3c7',color:'#92400e',fontSize:10,padding:'1px 6px',borderRadius:4,fontWeight:600}}>⚠️ REVISIONE SCADUTA</span>}
                            {item.alert === 'REVISIONE_IN_SCADENZA' && <span style={{background:'#fef9c3',color:'#713f12',fontSize:10,padding:'1px 6px',borderRadius:4,fontWeight:600}}>⚠️ REVISIONE IN SCADENZA</span>}
                            {item.alert === 'COLLAUDO_SCADUTO' && <span style={{background:'#fce7f3',color:'#831843',fontSize:10,padding:'1px 6px',borderRadius:4,fontWeight:600}}>🔴 COLLAUDO SCADUTO</span>}
                            {item.alert === 'COLLAUDO_IN_SCADENZA' && <span style={{background:'#fdf4ff',color:'#6b21a8',fontSize:10,padding:'1px 6px',borderRadius:4,fontWeight:600}}>⚠️ COLLAUDO IN SCADENZA</span>}
                          </div>
                        )}
                      </div>
                      {item.controllato && <span style={{ color:'#16a34a', fontSize:16 }}>✓</span>}
                    </div>
                    <div style={{ padding:'0 12px 10px 42px' }}>
                      <input type="text" placeholder="Nota..."
                        value={item.note||''}
                        onChange={e=> item.spot
                          ? setImpiantiSpot(l=>l.map(s=>s.id===item.spotId?{...s,note:e.target.value}:s))
                          : setNote(item._idx, e.target.value)}
                        onBlur={()=> item.spot &&
                          api.put(`/intervento-spot/${item.spotId}`, { note: item.note || '' }).catch(()=>{})}
                        style={{ width:'100%', padding:'5px 8px', fontSize:12, border:'1px solid #e5e7eb', borderRadius:4, background:'#f9fafb', boxSizing:'border-box' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {nTot === 0 && (
            <div style={{ textAlign:'center', padding:32, color:'#9ca3af' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>📋</div>
              Nessun impianto registrato per questo cliente.
            </div>
          )}

          {/* MATERIALI SOSTITUITI */}
          <div className="card" style={{ marginTop:16 }}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <span className="card-title">🔩 Materiali sostituiti</span>
              <button className="btn btn-secondary btn-sm" onClick={aggMateriale}>➕ Aggiungi</button>
            </div>

            {/* Ricerca prodotti FIC */}
            <input className="form-control" placeholder="🔍 Cerca nel catalogo FIC..."
              value={cercaProd} onChange={e=>setCercaProd(e.target.value)}
              style={{marginBottom:6,fontSize:13}} />
            {cercaProd && (
              <div style={{border:'1px solid #e5e7eb',borderRadius:6,maxHeight:150,overflowY:'auto',marginBottom:8,background:'#fff',fontSize:12}}>
                {prodottiFic.filter(p=>p.name?.toLowerCase().includes(cercaProd.toLowerCase())).slice(0,8).map(p=>(
                  <div key={p.id}
                    style={{padding:'6px 10px',cursor:'pointer',borderBottom:'1px solid #f3f4f6',display:'flex',gap:8}}
                    onMouseEnter={e=>e.currentTarget.style.background='#f9fafb'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}
                    onClick={()=>applicaProdFic(materiali.length-1, p)}>
                    <span style={{flex:1}}>{p.name}</span>
                    <span style={{color:'#6b7280'}}>€{parseFloat(p.net_price||0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            {materiali.map((m, i) => (
              <div key={i} style={{display:'flex',gap:6,marginBottom:6,alignItems:'center'}}>
                <input className="form-control" placeholder="Descrizione materiale"
                  value={m.descrizione||''} onChange={e=>setMat(i,'descrizione',e.target.value)}
                  style={{flex:2,fontSize:12,padding:'5px 8px'}} />
                <input type="number" className="form-control" placeholder="Qtà" min={1}
                  value={m.quantita||1} onChange={e=>setMat(i,'quantita',e.target.value)}
                  style={{width:55,fontSize:12,padding:'5px 6px',textAlign:'center'}} />
                <input type="number" className="form-control" placeholder="€" min={0} step={0.01}
                  value={m.prezzo_unitario||0} onChange={e=>setMat(i,'prezzo_unitario',e.target.value)}
                  style={{width:75,fontSize:12,padding:'5px 6px',textAlign:'right'}} />
                <button className="btn btn-danger btn-sm" onClick={()=>rimuMateriale(i)}>✕</button>
              </div>
            ))}
            {materiali.length===0 && <p style={{color:'#9ca3af',fontSize:12,textAlign:'center'}}>Nessun materiale aggiunto</p>}
          </div>

          {/* NOTE TECNICO */}
          <div className="card" style={{ marginTop:16 }}>
            <div className="card-title" style={{ marginBottom:8 }}>📝 Note del tecnico</div>
            <textarea className="form-control" rows={4}
              placeholder="Note generali sull'intervento, anomalie riscontrate, lavori eseguiti..."
              value={noteTecnico}
              onChange={e=>setNoteTecnico(e.target.value)}
              style={{ fontSize:14 }} />
          </div>

          <button className="btn btn-primary" onClick={salvaChecklist} disabled={saving}
            style={{ width:'100%', justifyContent:'center', padding:'14px', fontSize:15, marginTop:8 }}>
            {saving ? '...' : `✅ Salva e procedi alla firma (${nOk}/${nTot} controllati)`}
          </button>
        </div>
      )}

      {/* FIRMA */}
      {fase === 'firma' && (
        <div>
          <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:12, marginBottom:16, fontSize:13 }}>
            ✅ <strong>{nOk}/{nTot}</strong> impianti controllati. Fai firmare il cliente.
          </div>

          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-title" style={{ marginBottom:8 }}>👤 Nome firmatario</div>
            <input className="form-control" placeholder="Nome e cognome del cliente..."
              value={nomeFirma} onChange={e=>setNome(e.target.value)} />
          </div>

          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <span className="card-title">✍️ Firma cliente</span>
              <button className="btn btn-secondary btn-sm" onClick={()=>pulisciFirma('cliente')}>🗑 Cancella</button>
            </div>
            <canvas ref={canvasRef}
              style={{ width:'100%', height:180, border:'2px dashed #e5e7eb', borderRadius:8, touchAction:'none', cursor:'crosshair', background:'#fafafa' }} />
            <p style={{ fontSize:11, color:'#9ca3af', textAlign:'center', margin:'6px 0 0' }}>
              Firma del cliente
            </p>
          </div>

          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <span className="card-title">✍️ Firma tecnico</span>
              <button className="btn btn-secondary btn-sm" onClick={()=>pulisciFirma('tecnico')}>🗑 Cancella</button>
            </div>
            <div style={{ marginBottom:8 }}>
              <input className="form-control" placeholder="Nome del tecnico..."
                value={nomeTecnico} onChange={e=>setNomeTecnico(e.target.value)} style={{fontSize:13}} />
            </div>
            <canvas ref={canvasTecnicoRef}
              style={{ width:'100%', height:120, border:'2px dashed #e5e7eb', borderRadius:8, touchAction:'none', cursor:'crosshair', background:'#fafafa' }} />
            <p style={{ fontSize:11, color:'#9ca3af', textAlign:'center', margin:'6px 0 0' }}>
              Firma del tecnico
            </p>
          </div>

          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-secondary" onClick={()=>setFase('checklist')}>⬅ Torna</button>
            <button className="btn btn-primary" onClick={salvaFirma} disabled={saving || !nomeFirma}
              style={{ flex:1, justifyContent:'center', padding:'14px' }}>
              {saving ? '...' : '✅ Conferma e chiudi intervento'}
            </button>
          </div>
        </div>
      )}

      {/* COMPLETATO */}
      {fase === 'completato' && (
        <div>
          {/* VERBALE PDF */}
          <div id="verbale-pdf" style={{ marginBottom:16, background:'#fff', color:'#000', fontFamily:'Arial,sans-serif', fontSize:11, border:'1px solid #ccc' }}>

            {/* HEADER */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'10px 14px', borderBottom:'2px solid #b30000' }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:15, color:'#b30000' }}>Registro dei controlli antincendio</div>
                <div style={{ fontSize:11, color:'#b30000', fontStyle:'italic' }}>(D.M. 1° settembre 2021)</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <img src="/sosfire-logo.jpg" alt="SOS Fire" style={{ width:50, height:50, borderRadius:'50%', objectFit:'cover' }} />
                <div style={{ fontWeight:700, fontSize:11, marginTop:4 }}>SOS FIRE s.r.l.s.</div>
                <div style={{ fontSize:9, color:'#555' }}>Via Appia Nuova 771, 00178 Roma (RM)</div>
                <div style={{ fontSize:9, color:'#555' }}>P.IVA: 17876171004</div>
              </div>
            </div>

            {/* INTESTAZIONE CLIENTE */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', borderBottom:'1px solid #ccc' }}>
              <div style={{ padding:'8px 14px', borderRight:'1px solid #ccc' }}>
                <div style={{ fontSize:9, color:'#888', marginBottom:3 }}>SPETT.LE</div>
                <div style={{ fontWeight:700 }}>{inv.sito_nome}</div>
                <div>{inv.indirizzo || '—'}</div>
              </div>
              <div style={{ padding:'8px 14px' }}>
                <div style={{ fontSize:9, color:'#888', marginBottom:3 }}>SEDE OPERATIVA</div>
                <div style={{ fontWeight:700 }}>{inv.sito_nome}</div>
                <div>{inv.indirizzo || '—'}</div>
              </div>
            </div>

            {/* VERBALE INFO */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', borderBottom:'2px solid #b30000', background:'#f8f8f8' }}>
              <div style={{ padding:'6px 14px', borderRight:'1px solid #ccc' }}>
                <div style={{ fontSize:9, color:'#888' }}>VERBALE N°</div>
                <div style={{ fontWeight:700 }}>{inv.id}</div>
              </div>
              <div style={{ padding:'6px 14px', borderRight:'1px solid #ccc' }}>
                <div style={{ fontSize:9, color:'#888' }}>DATA INTERVENTO</div>
                <div style={{ fontWeight:700 }}>{fmtData(inv.data_intervento)}</div>
                {(oraInizio||inv.ora_inizio) && (
                  <div style={{ fontSize:10 }}>
                    Inizio: {(oraInizio||inv.ora_inizio||'').substring(0,5)} &nbsp;|&nbsp; Fine: {(oraFine||inv.ora_fine||'').substring(0,5)}
                  </div>
                )}
              </div>
              <div style={{ padding:'6px 14px' }}>
                <div style={{ fontSize:9, color:'#888' }}>TECNICO</div>
                <div style={{ fontWeight:700 }}>{nomeTecnico || '—'}</div>
              </div>
            </div>

            {/* RIEPILOGO PER TIPO */}
            {Object.entries(perTipo).map(([tipo, lista]) => {
              const nPos = lista.filter(i=>i.controllato).length;
              const nNeg = lista.filter(i=>!i.controllato).length;
              return (
                <div key={tipo} style={{ marginBottom:0 }}>
                  {/* Riepilogo */}
                  <div style={{ background:'#f0f0f0', padding:'4px 14px', fontWeight:700, fontSize:10, color:'#555', letterSpacing:1, borderBottom:'1px solid #ddd', borderTop:'2px solid #e0e0e0', textTransform:'uppercase' }}>
                    Riepilogo {TIPO_LABEL[tipo]||tipo}
                  </div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
                    <thead>
                      <tr style={{ background:'#f8f8f8' }}>
                        <th style={{ padding:'4px 14px', textAlign:'left', borderBottom:'1px solid #ddd', fontWeight:600 }}>CONTEGGIO</th>
                        <th style={{ padding:'4px 8px', textAlign:'left', borderBottom:'1px solid #ddd', fontWeight:600 }}>TIPO INTERVENTO</th>
                        <th style={{ padding:'4px 8px', textAlign:'center', borderBottom:'1px solid #ddd', fontWeight:600, width:50 }}>NR INT.</th>
                        <th style={{ padding:'4px 8px', textAlign:'center', borderBottom:'1px solid #ddd', fontWeight:600, width:40 }}>POS.</th>
                        <th style={{ padding:'4px 8px', textAlign:'center', borderBottom:'1px solid #ddd', fontWeight:600, width:40 }}>NEG.</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ padding:'3px 14px', borderBottom:'1px solid #eee' }}>{TIPO_LABEL[tipo]||tipo}</td>
                        <td style={{ padding:'3px 8px', borderBottom:'1px solid #eee' }}>Verifica</td>
                        <td style={{ padding:'3px 8px', textAlign:'center', borderBottom:'1px solid #eee' }}>{lista.length}</td>
                        <td style={{ padding:'3px 8px', textAlign:'center', borderBottom:'1px solid #eee' }}>{nPos}</td>
                        <td style={{ padding:'3px 8px', textAlign:'center', borderBottom:'1px solid #eee' }}>{nNeg}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Dettaglio */}
                  <div style={{ background:'#f0f0f0', padding:'4px 14px', fontWeight:700, fontSize:10, color:'#555', letterSpacing:1, borderTop:'1px solid #ddd', textTransform:'uppercase' }}>
                    Dettaglio {TIPO_LABEL[tipo]||tipo}
                  </div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
                    <thead>
                      <tr style={{ background:'#f8f8f8' }}>
                        <th style={{ padding:'4px 8px', textAlign:'center', borderBottom:'1px solid #ddd', width:30, fontWeight:600 }}>N°</th>
                        <th style={{ padding:'4px 8px', textAlign:'left', borderBottom:'1px solid #ddd', fontWeight:600 }}>DISPOSITIVO</th>
                        <th style={{ padding:'4px 8px', textAlign:'left', borderBottom:'1px solid #ddd', fontWeight:600 }}>MATRICOLA</th>
                        <th style={{ padding:'4px 8px', textAlign:'center', borderBottom:'1px solid #ddd', width:50, fontWeight:600 }}>ANNO F.</th>
                        <th style={{ padding:'4px 8px', textAlign:'left', borderBottom:'1px solid #ddd', fontWeight:600 }}>UBICAZIONE</th>
                        <th style={{ padding:'4px 8px', textAlign:'center', borderBottom:'1px solid #ddd', width:60, fontWeight:600 }}>ESITO</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lista.map((item, idx) => {
                        const parts = item.label.split('|').map(s=>s.trim());
                        const numMatch = item.label.match(/^N\.(\w+)/);
                        const num = numMatch ? numMatch[1] : idx+1;
                        const dispositivo = parts[1] || parts[0] || item.label;
                        const matricola = parts[2] || '—';
                        const luogo = parts[parts.length-1] || '—';
                        const luogoCompleto = item.piano
                          ? `${luogo}${luogo && luogo !== '—' ? ' · ' : ''}Piano ${item.piano}`
                          : luogo;
                        const annoFab = item.anno_produzione || item.anno_costruzione || '';
                        const scad = item.anno_collaudo || item.scadenza || '';
                        return (
                          <React.Fragment key={item.id}>
                            <tr style={{ borderBottom:'1px solid #f0f0f0' }}>
                              <td style={{ padding:'3px 8px', textAlign:'center', fontWeight:600 }}>{num}</td>
                              <td style={{ padding:'3px 8px' }}>{dispositivo}</td>
                              <td style={{ padding:'3px 8px' }}>{matricola}</td>
                              <td style={{ padding:'3px 8px', textAlign:'center' }}>{annoFab || scad}</td>
                              <td style={{ padding:'3px 8px' }}>{luogoCompleto}</td>
                              <td style={{ padding:'3px 8px', textAlign:'center', color: item.controllato?'green':'red', fontWeight:600 }}>
                                {item.controllato ? 'Positivo' : 'Negativo'}
                              </td>
                            </tr>
                            <tr style={{ borderBottom:'1px solid #eee', background:'#fafafa' }}>
                              <td></td>
                              <td colSpan={5} style={{ padding:'2px 8px', fontSize:9, color:'#555', fontStyle:'italic' }}>
                                {fmtData(inv.data_intervento)} &nbsp; Verifica &nbsp; Esito: {item.controllato ? 'Positivo' : 'Negativo'}
                                {item.note ? ` — ${item.note}` : ''}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}

            {/* NOTE */}
            {(Object.values(perTipo).flat().some(i=>i.note) || noteTecnico || inv.note_tecnico) && (
              <div style={{ border:'1px solid #ddd', margin:'8px 14px', padding:'8px' }}>
                <div style={{ background:'#f0f0f0', fontWeight:700, fontSize:10, color:'#555', letterSpacing:1, padding:'3px 6px', marginBottom:6, textTransform:'uppercase' }}>Note</div>
                {Object.entries(perTipo).map(([tipo, lista]) =>
                  lista.filter(i=>i.note).map(item => (
                    <div key={item.id} style={{ fontSize:10, marginBottom:3 }}>
                      <strong>{TIPO_LABEL[tipo]||tipo}</strong> — {item.label.split('|')[0].trim()} — N.{item.label.match(/^N\.(\w+)/)?.[1]||'?'}: <em>{item.note}</em>
                    </div>
                  ))
                )}
                {(noteTecnico||inv.note_tecnico) && (
                  <div style={{ fontSize:10, marginTop:6 }}>
                    <strong>Note del tecnico:</strong><br/>
                    <em>{noteTecnico||inv.note_tecnico}</em>
                  </div>
                )}
              </div>
            )}

            {/* FIRME */}
            <FirmaDisplay interventoId={id} nomeTecnico={nomeTecnico} />

          </div>

          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <button className="btn btn-primary" onClick={stampaPDF} style={{ flex:1, justifyContent:'center' }}>
              🖨️ Stampa / Salva PDF
            </button>
            <button className="btn btn-secondary" onClick={scaricaLettera} disabled={letteraLoading}
              style={{ flex:1, justifyContent:'center' }}>
              {letteraLoading ? '...' : '📄 Scarica lettera'}
            </button>
            <button className="btn btn-secondary" onClick={preparaBozza} disabled={bozzaLoading}
              style={{ flex:1, justifyContent:'center' }}>
              {bozzaLoading ? '...' : '📧 Prepara bozza email'}
            </button>
            {materiali.filter(m=>m.descrizione?.trim()).length > 0 && (
              <button className="btn btn-secondary" onClick={generaDdt} disabled={dttLoading}
                style={{ flex:1, justifyContent:'center' }}>
                {dttLoading ? '...' : `📦 Genera DDT (${materiali.filter(m=>m.descrizione?.trim()).length} mat.)`}
              </button>
            )}
            <button className="btn btn-secondary" onClick={()=>navigate('/interventi')}>
              ← Lista
            </button>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .sidebar, .topbar, .btn, button, .no-print { display: none !important; }
          #verbale-pdf { box-shadow: none !important; border: none !important; }
          body { background: white !important; }
        }
      `}</style>

      {modImpianto && (
        <ModaleImpianto
          tipo={modImpianto.tipo}
          impiantoId={modImpianto.id}
          onClose={()=>setModImpianto(null)}
          onSaved={()=>{ setModImpianto(null); carica(); }}
        />
      )}
      {modaleSpot && (
        <ModaleImpiantoSpot
          onClose={()=>setModaleSpot(false)}
          onSalva={aggiungiSpot}
        />
      )}

      {firmaDdtOpen && (
        <FirmaDdtModal
          onClose={()=>setFirmaDdtOpen(false)}
          onConfirm={confermaDdtConFirma}
        />
      )}
    </div>
  );
}

// ── MODALE FIRMA DDT ──────────────────────────────────────────
function FirmaDdtModal({ onClose, onConfirm }) {
  const canvasRef = React.useRef(null);
  const [vuoto, setVuoto] = React.useState(true);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    let disegna = false;
    const pos = e => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const start = e => { e.preventDefault(); disegna = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const draw = e => { if (!disegna) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setVuoto(false); };
    const stop = () => { disegna = false; };
    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stop);
    canvas.addEventListener('touchstart', start, {passive:false});
    canvas.addEventListener('touchmove', draw, {passive:false});
    canvas.addEventListener('touchend', stop);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stop);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stop);
    };
  }, []);

  const pulisci = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0,0,c.width,c.height);
    setVuoto(true);
  };

  const conferma = () => {
    const dataUrl = vuoto ? null : canvasRef.current.toDataURL('image/png');
    onConfirm(dataUrl);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}
      onClick={onClose}>
      <div style={{background:'#fff',borderRadius:12,padding:24,width:'100%',maxWidth:520}}
        onClick={e=>e.stopPropagation()}>
        <h3 style={{margin:'0 0 4px',fontSize:16}}>✍️ Firma del cliente</h3>
        <p style={{margin:'0 0 12px',fontSize:13,color:'#6b7280'}}>
          Fai firmare il cliente per la consegna dei materiali (DDT).
        </p>
        <canvas ref={canvasRef}
          style={{width:'100%',height:200,border:'2px dashed #cbd5e1',borderRadius:8,touchAction:'none',background:'#fafafa',cursor:'crosshair'}} />
        <div style={{display:'flex',gap:8,marginTop:16}}>
          <button className="btn btn-secondary" onClick={pulisci}>🗑 Cancella</button>
          <div style={{flex:1}} />
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={conferma}>
            ✅ Conferma e genera DDT
          </button>
        </div>
        <p style={{margin:'10px 0 0',fontSize:11,color:'#9ca3af',textAlign:'center'}}>
          Puoi anche generare il DDT senza firma cliccando "Conferma".
        </p>
      </div>
    </div>
  );
}

function FirmaDisplay({ interventoId, nomeTecnico }) {
  const [firma, setFirma] = useState(null);

  useEffect(() => {
    api.get(`/intervento/${interventoId}/firma`)
      .then(r => setFirma(r.data?.firma))
      .catch(()=>{});
  }, [interventoId]);

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0, borderTop:'2px solid #b30000', margin:'8px 0 0' }}>
      {/* Firma tecnico con timbro sovrapposto */}
      <div style={{ padding:'12px 14px', borderRight:'1px solid #ccc', textAlign:'center' }}>
        <div style={{ fontSize:10, color:'#555', marginBottom:8, fontWeight:600 }}>Firma del tecnico</div>
        <div style={{ position:'relative', borderBottom:'1px dashed #aaa', marginBottom:6, minHeight:110 }}>
          <img src="/timbro-sosfire.png" alt="Timbro SOS Fire"
            style={{ position:'absolute', bottom:4, left:'50%', transform:'translateX(-50%)', width:150, height:'auto', opacity:0.25, zIndex:1 }} />
          <div style={{ position:'relative', zIndex:2, display:'flex', alignItems:'flex-end', justifyContent:'center', minHeight:100, paddingBottom:6 }}>
            {firma?.firma_tecnico_base64 ? (
              <img src={firma.firma_tecnico_base64} alt="Firma tecnico"
                style={{ maxHeight:80, maxWidth:'80%' }} />
            ) : (
              <div style={{ height:70 }}></div>
            )}
          </div>
        </div>
        <div style={{ fontSize:10, color:'#333' }}>{firma?.nome_tecnico || nomeTecnico || '—'}</div>
      </div>
      {/* Firma cliente */}
      <div style={{ padding:'12px 14px', textAlign:'center' }}>
        <div style={{ fontSize:10, color:'#555', marginBottom:8, fontWeight:600 }}>Firma del cliente</div>
        <div style={{ borderBottom:'1px dashed #aaa', marginBottom:6, minHeight:80, display:'flex', alignItems:'flex-end', justifyContent:'center', paddingBottom:4 }}>
          {firma?.firma_base64 ? (
            <img src={firma.firma_base64} alt="Firma cliente"
              style={{ maxHeight:70, maxWidth:'80%' }} />
          ) : (
            <div style={{ height:60 }}></div>
          )}
        </div>
        <div style={{ fontSize:10, color:'#333' }}>{firma?.nome_firmatario || '—'}</div>
      </div>
    </div>
  );
}

// ── AGGIUNTA IMPIANTO OCCASIONALE ─────────────────────────────
// Impianto presente solo in questo intervento: non entra nel censimento del cliente.
function ModaleImpiantoSpot({ onClose, onSalva }) {
  const TIPI = [
    ['estintori',        '🧯 Estintori'],
    ['idranti',          '🚒 Idranti / Naspi'],
    ['porte',            '🚪 Porte REI/U.S./Portoni scorrevoli'],
    ['rivelazione_fumi', '💨 Rivelazione fumi'],
    ['illuminazione_emergenza', '💡 Luci di emergenza'],
    ['sprinkler',        '🌊 Sprinkler'],
    ['gruppi_pompa',     '⚙️ Gruppi pompa'],
    ['cassette_mediche', '🩺 Cassette pronto soccorso'],
    ['dpi',              '🦺 DPI'],
    ['__altro',          '✏️ Altro (scrivi tu)'],
  ];

  const [tipo, setTipo]       = React.useState('estintori');
  const [tipoLibero, setLib]  = React.useState('');
  const [form, setForm]       = React.useState({
    descrizione:'', matricola:'', numero:'', luogo:'', anno:'', note:'',
  });
  const [salvando, setSalv]   = React.useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const conferma = async () => {
    const tipoFinale = tipo === '__altro' ? (tipoLibero.trim() || 'altro') : tipo;
    if (!form.descrizione.trim() && !form.matricola.trim()) {
      alert('Indica almeno la descrizione o la matricola.');
      return;
    }
    setSalv(true);
    await onSalva({ ...form, tipo_impianto: tipoFinale });
    setSalv(false);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',
                 alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}
      onClick={onClose}>
      <div style={{background:'#fff',borderRadius:12,padding:24,width:'100%',maxWidth:500,
                   maxHeight:'90vh',overflowY:'auto'}}
        onClick={e=>e.stopPropagation()}>
        <h3 style={{margin:'0 0 4px',fontSize:16}}>➕ Impianto in questo intervento</h3>
        <p style={{margin:'0 0 16px',fontSize:12,color:'#6b7280'}}>
          Vale solo per questo intervento: non viene aggiunto agli impianti del cliente.
        </p>

        <label className="form-label">Tipo</label>
        <select className="form-control" value={tipo} onChange={e=>setTipo(e.target.value)}>
          {TIPI.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>

        {tipo === '__altro' && (
          <div style={{marginTop:8}}>
            <label className="form-label">Descrizione del tipo</label>
            <input className="form-control" placeholder="es. Rivelazione gas cucina"
              value={tipoLibero} onChange={e=>setLib(e.target.value)} />
          </div>
        )}

        <div style={{marginTop:12}}>
          <label className="form-label">Descrizione</label>
          <input className="form-control" placeholder="es. Estintore polvere 6 kg"
            value={form.descrizione} onChange={e=>set('descrizione', e.target.value)} />
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginTop:8}}>
          <div>
            <label className="form-label">N°</label>
            <input className="form-control" value={form.numero} onChange={e=>set('numero', e.target.value)} />
          </div>
          <div>
            <label className="form-label">Matricola</label>
            <input className="form-control" value={form.matricola} onChange={e=>set('matricola', e.target.value)} />
          </div>
          <div>
            <label className="form-label">Anno</label>
            <input className="form-control" value={form.anno} onChange={e=>set('anno', e.target.value)} />
          </div>
        </div>

        <div style={{marginTop:8}}>
          <label className="form-label">Ubicazione</label>
          <input className="form-control" value={form.luogo} onChange={e=>set('luogo', e.target.value)} />
        </div>

        <div style={{marginTop:8}}>
          <label className="form-label">Note</label>
          <textarea className="form-control" rows={2}
            value={form.note} onChange={e=>set('note', e.target.value)} />
        </div>

        <div style={{display:'flex',gap:8,marginTop:18}}>
          <button className="btn btn-secondary" style={{flex:1}} onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" style={{flex:1}} onClick={conferma} disabled={salvando}>
            {salvando ? '...' : '➕ Aggiungi'}
          </button>
        </div>
      </div>
    </div>
  );
}
